import React, { useState, useEffect, useRef, useCallback } from "react";
import { Activity, Upload, ShieldCheck, AlertTriangle, CheckCircle2, FileSpreadsheet, Search } from "lucide-react";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const NAVY = "hsl(217_63%_15%)";
const GREEN = "hsl(158_64%_28%)";
const CLIENT_ROLES = ["client_admin", "client_user"];

const SEV_COLOR = {
  high: "bg-[hsl(354_78%_94%)] text-[hsl(354_60%_45%)]",
  medium: "bg-[hsl(38_92%_90%)] text-[hsl(30_80%_38%)]",
  low: "bg-secondary text-muted-foreground",
};
const ST_COLOR = {
  passed: "text-[hsl(158_64%_30%)]", warning: "text-[hsl(30_80%_38%)]", exception: "text-[hsl(354_60%_45%)]",
};

function Metric({ label, value, color, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card p-4">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: color || NAVY }}>{value}</div>
    </div>
  );
}

export default function VatControl() {
  const { t, lang, fmt } = useI18n();
  const { user } = useAuth();
  const isClient = CLIENT_ROLES.includes(user?.role);
  const isMgmt = ["platform_admin", "management"].includes(user?.role);

  const [clients, setClients] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [health, setHealth] = useState(null);
  const [txns, setTxns] = useState([]);
  const [excs, setExcs] = useState([]);
  const [txStatus, setTxStatus] = useState("all");
  const [txQ, setTxQ] = useState("");
  const salesRef = useRef(null);
  const purchRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const orgParam = useCallback(() => (isClient ? {} : { org_id: orgId }), [isClient, orgId]);

  useEffect(() => {
    if (isMgmt) api.get("/admin/clients").then((r) => setClients(Array.isArray(r.data) ? r.data : (r.data.clients || []))).catch(() => {});
  }, [isMgmt]);

  const loadPeriods = useCallback(() => {
    if (!isClient && !orgId) return;
    api.get("/control/periods", { params: orgParam() }).then((r) => setPeriods(r.data || [])).catch((e) => toast.error(apiError(e)));
  }, [isClient, orgId, orgParam]);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  const refresh = useCallback(() => {
    if (!periodId) { setHealth(null); setTxns([]); setExcs([]); return; }
    const p = { params: orgParam() };
    api.get(`/control/periods/${periodId}/health`, p).then((r) => setHealth(r.data)).catch(() => {});
    const tp = { params: { ...orgParam(), ...(txStatus !== "all" ? { status: txStatus } : {}), ...(txQ ? { q: txQ } : {}) } };
    api.get(`/control/periods/${periodId}/transactions`, tp).then((r) => setTxns(r.data || [])).catch(() => {});
    api.get(`/control/periods/${periodId}/exceptions`, p).then((r) => setExcs(r.data || [])).catch(() => {});
  }, [periodId, orgParam, txStatus, txQ]);

  useEffect(() => { refresh(); }, [refresh]);

  const createPeriod = async () => {
    if (!newLabel.trim()) return;
    if (!isClient && !orgId) { toast.error(t("ctrl_pick_client_first")); return; }
    try {
      const r = await api.post("/control/periods", { ...(isClient ? {} : { org_id: orgId }), label: newLabel.trim() });
      setNewLabel(""); setPeriods((p) => [r.data, ...p]); setPeriodId(r.data.id); toast.success(t("ctrl_created"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const ingest = async (file, ledgerType) => {
    if (!file || !periodId) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("ledger_type", ledgerType);
    if (!isClient && orgId) fd.append("org_id", orgId);
    try {
      const r = await api.post(`/control/periods/${periodId}/ingest`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.rows_ingested} ${t("ctrl_rows_in")} · ${r.data.exceptions_created} ${t("ctrl_exc_created")}`);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resolveExc = async (eid, status) => {
    try {
      await api.post(`/control/exceptions/${eid}/resolve`, { ...(isClient ? {} : { org_id: orgId }), status, resolution: status === "RESOLVED" ? "Resolved by reviewer" : "Escalated to advisory" });
      toast.success(t("ctrl_updated")); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toAdvisory = async (e) => {
    try {
      const r = await api.post(`/control/exceptions/${e.id}/to-advisory`, { ...(isClient ? {} : { org_id: orgId }), scope: lang === "ar" ? "استشارة ناشئة من استثناء رقابي" : "Advisory arising from a control exception" });
      toast.success(`${t("ctrl_adv_created")}: ${r.data.reference}`); refresh();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5 gc-fade-up" data-testid="vat-control">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: NAVY }}><Activity className="h-5 w-5 text-white" /></div>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold" style={{ color: NAVY }}>{t("ctrl_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("ctrl_sub")}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-end gap-3">
        {isMgmt && (
          <div>
            <label className="text-xs text-muted-foreground">{t("ctrl_client")}</label>
            <Select value={orgId} onValueChange={(v) => { setOrgId(v); setPeriodId(""); }}>
              <SelectTrigger data-testid="ctrl-client-select" className="w-64 h-9"><SelectValue placeholder={t("ctrl_pick_client")} /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name || c.name || c.company_name || c.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <label className="text-xs text-muted-foreground">{t("ctrl_period")}</label>
          <Select value={periodId} onValueChange={setPeriodId} disabled={!periods.length}>
            <SelectTrigger data-testid="ctrl-period-select" className="w-56 h-9"><SelectValue placeholder={t("ctrl_no_period")} /></SelectTrigger>
            <SelectContent>
              {periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-muted-foreground">{t("ctrl_new_period")}</label>
            <Input data-testid="ctrl-new-period" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="May 2026" className="w-40 h-9" />
          </div>
          <Button data-testid="ctrl-create-period" onClick={createPeriod} variant="outline" className="rounded-full h-9">{t("ctrl_create")}</Button>
        </div>
      </div>

      {periodId && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <input ref={salesRef} type="file" accept=".csv,.xlsx,.xls" hidden data-testid="ctrl-sales-file" onChange={(e) => { const f = e.target.files?.[0]; if (f) { ingest(f, "sales"); e.target.value = ""; } }} />
            <input ref={purchRef} type="file" accept=".csv,.xlsx,.xls" hidden data-testid="ctrl-purch-file" onChange={(e) => { const f = e.target.files?.[0]; if (f) { ingest(f, "purchase"); e.target.value = ""; } }} />
            <Button data-testid="ctrl-upload-sales" onClick={() => salesRef.current?.click()} disabled={busy} className="rounded-full text-white" style={{ backgroundColor: NAVY }}><Upload className="h-4 w-4 me-1.5" />{t("ctrl_upload_sales")}</Button>
            <Button data-testid="ctrl-upload-purch" onClick={() => purchRef.current?.click()} disabled={busy} variant="outline" className="rounded-full"><FileSpreadsheet className="h-4 w-4 me-1.5" />{t("ctrl_upload_purch")}</Button>
          </div>

          {health && (
            <div>
              <div className="flex items-center gap-2 mb-2"><ShieldCheck className="h-4 w-4" style={{ color: GREEN }} /><span className="text-sm font-semibold" style={{ color: NAVY }}>{t("ctrl_health")}</span></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ctrl-health">
                <Metric testid="ctrl-m-tested" label={t("ctrl_tested")} value={health.transactions_tested} />
                <Metric testid="ctrl-m-passed" label={t("ctrl_passed")} value={health.transactions_passed} color={GREEN} />
                <Metric testid="ctrl-m-warn" label={t("ctrl_warnings")} value={health.transactions_with_warnings} color="hsl(30 80% 42%)" />
                <Metric testid="ctrl-m-exc" label={t("ctrl_exceptions")} value={health.transactions_with_exceptions} color="hsl(354 60% 45%)" />
                <Metric testid="ctrl-m-recon" label={t("ctrl_recon")} value={`${health.reconciliation_completion}%`} />
                <Metric testid="ctrl-m-material" label={t("ctrl_material")} value={health.material_exceptions} color="hsl(354 60% 45%)" />
                <Metric testid="ctrl-m-high" label={t("ctrl_high_risk")} value={health.unresolved_high_risk} color="hsl(354 60% 45%)" />
                <Metric testid="ctrl-m-review" label={t("ctrl_review")} value={health.tax_review_required} color="hsl(30 80% 42%)" />
              </div>
              <div className="mt-3 rounded-xl p-3 inline-flex flex-wrap gap-6" style={{ backgroundColor: "hsl(158 64% 96%)" }}>
                <div><div className="text-[11px] text-muted-foreground">{t("ctrl_sales_vat")}</div><div className="font-bold" style={{ color: NAVY }}>{fmt(health.totals.sales_vat)}</div></div>
                <div><div className="text-[11px] text-muted-foreground">{t("ctrl_purch_vat")}</div><div className="font-bold" style={{ color: NAVY }}>{fmt(health.totals.purchase_vat)}</div></div>
                <div><div className="text-[11px] text-muted-foreground">{t("ctrl_net_vat")}</div><div className="font-bold" style={{ color: GREEN }}>{fmt(health.totals.net_vat)} SAR</div></div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <span className="text-sm font-semibold" style={{ color: NAVY }}>{t("ctrl_tx_explorer")}</span>
                <div className="flex items-center gap-2">
                  <div className="relative"><Search className="h-3.5 w-3.5 absolute top-2.5 start-2 text-muted-foreground" /><Input data-testid="ctrl-tx-search" value={txQ} onChange={(e) => setTxQ(e.target.value)} placeholder={t("ctrl_search_inv")} className="h-8 w-32 ps-7 text-xs" /></div>
                  <Select value={txStatus} onValueChange={setTxStatus}>
                    <SelectTrigger data-testid="ctrl-tx-status" className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("ctrl_all")}</SelectItem>
                      <SelectItem value="passed">{t("ctrl_passed")}</SelectItem>
                      <SelectItem value="warning">{t("ctrl_warnings")}</SelectItem>
                      <SelectItem value="exception">{t("ctrl_exceptions")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="max-h-96 overflow-auto">
                {txns.length === 0 ? <p className="text-sm text-muted-foreground">{t("ctrl_no_tx")}</p> : (
                  <table className="w-full text-xs">
                    <thead><tr className="text-muted-foreground text-start"><th className="text-start py-1">{t("ctrl_col_invoice")}</th><th className="text-start">{t("ctrl_col_ledger")}</th><th className="text-end">{t("ctrl_col_base")}</th><th className="text-end">{t("ctrl_col_vat")}</th><th className="text-end">{t("ctrl_col_status")}</th></tr></thead>
                    <tbody>
                      {txns.map((tx) => (
                        <tr key={tx.id} data-testid={`ctrl-tx-${tx.id}`} className="border-t border-border">
                          <td className="py-1.5 font-medium">{tx.invoice_number || "-"}</td>
                          <td className="text-muted-foreground">{tx.ledger_type}</td>
                          <td className="text-end">{fmt(tx.taxable_amount)}</td>
                          <td className="text-end">{fmt(tx.vat_amount)}</td>
                          <td className={`text-end font-semibold ${ST_COLOR[tx.status] || ""}`}>{t("ctrl_" + (tx.status === "warning" ? "warnings" : tx.status === "exception" ? "exceptions" : "passed"))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-4 w-4 text-[hsl(354_60%_45%)]" /><span className="text-sm font-semibold" style={{ color: NAVY }}>{t("ctrl_exceptions_title")}</span></div>
              <div className="max-h-96 overflow-auto space-y-2">
                {excs.length === 0 ? <p className="text-sm text-muted-foreground">{t("ctrl_no_exc")}</p> : excs.map((e) => (
                  <div key={e.id} data-testid={`ctrl-exc-${e.id}`} className="rounded-xl border border-border p-2.5 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${SEV_COLOR[e.severity]}`}>{e.severity} · {e.category}</span>
                      <span className="text-muted-foreground">{e.status}</span>
                    </div>
                    <p className="mt-1 text-muted-foreground">{lang === "ar" ? e.description_ar : e.description_en}</p>
                    {["OPEN", "UNDER REVIEW", "INFORMATION REQUIRED", "CLIENT CLARIFICATION"].includes(e.status) && (
                      <div className="flex gap-2 mt-2">
                        <Button data-testid={`ctrl-resolve-${e.id}`} onClick={() => resolveExc(e.id, "RESOLVED")} variant="outline" className="h-7 rounded-full text-[11px] px-3"><CheckCircle2 className="h-3 w-3 me-1" />{t("ctrl_resolve")}</Button>
                        {!isClient && <Button data-testid={`ctrl-escalate-${e.id}`} onClick={() => toAdvisory(e)} variant="ghost" className="h-7 rounded-full text-[11px] px-3">{t("ctrl_to_advisory")}</Button>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
      {!periodId && <p className="text-sm text-muted-foreground" data-testid="ctrl-empty">{t("ctrl_start_hint")}</p>}
    </div>
  );
}
